import React, { useState, useCallback, useOptimistic } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useNotification, Dialog } from '@dynamon-as/ui';
import { useRequests, RequestStatus } from '@features/requests';
import { useRequestComments } from '@features/requests/hooks';
import { useEmployeeId } from '@shared/hooks';
import {
    RequestDetails,
    RequestChatLayout,
    RequestChat,
} from '@features/requests/components';
import { toEmployeeMessages } from '@features/requests/utils';
import {
    NOTIFICATION_MESSAGES,
    RESUBMIT_DIALOG,
} from '@features/requests/constants';
import styles from './RequestDetail.module.css';

/**
 * Request Detail Page
 * Page for viewing details of a specific request
 */
export default function RequestDetail(): React.ReactElement {
    // Get request ID from URL params
    const { id } = useParams<{ id: string }>();

    // Router navigation
    const navigate = useNavigate();

    // Load orgEmployee and get orgEmployeeId using useEmployeeId hook
    // This triggers the org-employee fetch so orgEmployeeId is available on direct navigation
    const { orgEmployeeId, isLoading: isOrgEmployeeLoading } = useEmployeeId();

    // Use the requests hook with orgEmployeeId and requestId
    // This allows direct loading of a specific request without fetching all requests first
    const {
        requests,
        selectedRequest,
        isLoading: isRequestsLoading,
        cancelRequest: cancelRequestStore,
        submitRequest: submitRequestHook,
    } = useRequests(orgEmployeeId, id);

    // Use Notification from @dynamon-as/ui
    const { notify } = useNotification();

    // Use request comments hook for chat functionality
    const { addComment: addCommentApi } = useRequestComments(id);

    // Check if we're still loading orgEmployee or the request
    const isLoading =
        isOrgEmployeeLoading ?? isRequestsLoading ?? !orgEmployeeId;

    /**
     * Handles back navigation
     */
    const handleBack = (): void => {
        navigate('/requests');
    };

    /**
     * Handles edit action
     */
    const handleEdit = (): void => {
        if (id) {
            navigate(`/requests/${id}/edit`);
        }
    };

    /**
     * Handles cancel action
     */
    const handleCancel = (): void => {
        if (id) {
            const request = requests.find((r): boolean => r.id === id);
            if (request) {
                const cancelledRequest = cancelRequestStore(id);
                if (cancelledRequest) {
                    notify.success(NOTIFICATION_MESSAGES.REQUEST_CANCELLED);
                    navigate('/requests');
                }
            }
        }
    };

    /**
     * Handles submit action
     */
    const [showSubmitDialog, setShowSubmitDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = (): void => {
        if (id) {
            const request = requests.find((r): boolean => r.id === id);
            const displayReq = selectedRequest ?? request;
            const canSubmitOrResubmit =
                displayReq &&
                (displayReq.status === RequestStatus.DRAFT ||
                    displayReq.status === RequestStatus.REJECTED);
            if (canSubmitOrResubmit) {
                setShowSubmitDialog(true);
            }
        }
    };

    const handleCloseSubmitDialog = (): void => {
        setShowSubmitDialog(false);
    };

    const handleSubmitConfirm = useCallback(async (): Promise<void> => {
        setIsSubmitting(true);
        if (id) {
            const request = requests.find((r): boolean => r.id === id);
            if (
                request &&
                (request.status === RequestStatus.DRAFT ||
                    request.status === RequestStatus.REJECTED)
            ) {
                try {
                    const result = await submitRequestHook(id);
                    if (result.success) {
                        notify.success(NOTIFICATION_MESSAGES.REQUEST_SENT, {
                            duration: 5000,
                        });
                        setIsSubmitting(false);
                        setShowSubmitDialog(false);
                        // Navigate back to requests list, showing drafts tab
                        navigate('/requests');
                    } else {
                        notify.error(result.error ?? 'Feil ved innsending', {
                            duration: 5000,
                        });
                        setIsSubmitting(false);
                        // Close the dialog; the notification reports the error
                        setShowSubmitDialog(false);
                    }
                } catch (error) {
                    const errorMessage =
                        error instanceof Error
                            ? error.message
                            : 'Feil ved innsending';
                    notify.error(errorMessage, { duration: 5000 });
                    setIsSubmitting(false);
                    // Close the dialog; the notification reports the error
                    setShowSubmitDialog(false);
                }
            } else {
                setIsSubmitting(false);
                setShowSubmitDialog(false);
            }
        } else {
            setIsSubmitting(false);
            setShowSubmitDialog(false);
        }
    }, [id, navigate, notify, requests, submitRequestHook]);

    // Check if request exists
    const request = id ? requests.find((r): boolean => r.id === id) : null;

    // Use selectedRequest if available, otherwise use the request from the list
    const displayRequest = selectedRequest ?? request;

    // Whether the current request is a declined request being resubmitted
    const isResubmission = displayRequest?.status === RequestStatus.REJECTED;

    // Base messages from request transactions
    const baseMessages = toEmployeeMessages(displayRequest?.transactions);

    // Use optimistic updates for comments
    const [optimisticMessages, addOptimisticMessage] = useOptimistic(
        baseMessages,
        (
            currentMessages: typeof baseMessages,
            newMessage: string,
        ): typeof baseMessages => {
            return [
                ...currentMessages,
                {
                    id: `optimistic-${Date.now()}`,
                    message: newMessage,
                    registered: new Date(),
                    status: 'COMMENTED',
                    priority: 0,
                },
            ];
        },
    );

    // Handle sending a comment through the chat
    const handleAddComment = useCallback(
        async (text: string): Promise<boolean> => {
            const trimmed = text.trim();
            if (!trimmed) {
                return false;
            }

            // Add optimistic message
            addOptimisticMessage(trimmed);

            try {
                const ok = await addCommentApi(trimmed);
                if (ok) {
                    notify.success('Kommentar lagt til');
                } else {
                    notify.error('Kunne ikke sende kommentar');
                }
                return ok;
            } catch {
                notify.error('Kunne ikke sende kommentar');
                return false;
            }
        },
        [addCommentApi, notify, addOptimisticMessage],
    );

    // Check if we're still loading
    if (isLoading && !displayRequest) {
        return (
            <div className={styles.container}>
                <p>Laster søknad...</p>
            </div>
        );
    }

    // Only show "not found" after we've tried to load and there's no request
    if (!isLoading && !request && !selectedRequest && !displayRequest) {
        return (
            <div className={styles.container}>
                <p>Søknad ikke funnet</p>
                <button onClick={handleBack}>Tilbake til listen</button>
            </div>
        );
    }

    if (!displayRequest) {
        return (
            <div className={styles.container}>
                <p>Laster søknad...</p>
            </div>
        );
    }

    return (
        <RequestChatLayout
            chat={
                <RequestChat
                    messages={optimisticMessages}
                    onSend={handleAddComment}
                    isSending={false}
                />
            }
            chatBadge={optimisticMessages.length}
        >
            <RequestDetails
                request={displayRequest}
                onBack={handleBack}
                onEdit={handleEdit}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
            />
            <Dialog
                open={showSubmitDialog}
                onClose={handleCloseSubmitDialog}
                title={
                    isResubmission
                        ? RESUBMIT_DIALOG.TITLE
                        : 'Bekreft innsending'
                }
                onConfirm={handleSubmitConfirm}
                confirmText={
                    isSubmitting
                        ? 'Sender...'
                        : isResubmission
                          ? RESUBMIT_DIALOG.CONFIRM
                          : 'Send inn'
                }
                cancelText="Avbryt"
                danger={false}
                isLoading={isSubmitting}
                disabled={isSubmitting}
                size="sm"
            >
                <p>
                    {isResubmission
                        ? RESUBMIT_DIALOG.TEXT
                        : 'Er du sikker på at du vil sende inn denne søknaden?'}
                </p>
            </Dialog>
        </RequestChatLayout>
    );
}
